import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ShoppingCart, X } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';

import ConfirmationDialog from './ui/ConfirmationDialog';

const BAR_BORDER = 'rgb(38, 43, 49)';

export default function FullScreenModal(props) {
    const {
        open,
        onClose,
        currentStep,
        totalSteps,
        subtotal,
        stepLabel,
        seatsPickedCount,
        showControlBar,
        showReviewCart,
        showBackButton,
        showContinueButton,
        disableContinue,
        showConfirmOnClose,
        onBack,
        onContinue,
        onReviewCart,
        timer,
        currency = 'CAD',
    } = props;

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const { i18n } = useLingui();

    const formatCurrency = (value) => i18n.number(value, { style: 'currency', currency });

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    const handleCloseClick = useCallback(() => {
        if (showConfirmOnClose && seatsPickedCount > 0) {
            setConfirmDialogOpen(true);
        } else {
            onClose();
        }
    }, [showConfirmOnClose, seatsPickedCount, onClose]);

    function handleConfirmClose() {
        setConfirmDialogOpen(false);
        onClose();
    }

    function handleCancelClose() {
        setConfirmDialogOpen(false);
    }

    useEffect(() => {
        if (!open || confirmDialogOpen) {
            return undefined;
        }
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                handleCloseClick();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, confirmDialogOpen, handleCloseClick]);

    const stepIndicator =
        currentStep && totalSteps ? t`Step ${currentStep} of ${totalSteps}: ${stepLabel}` : '';

    const modal = open ? (
        <div
            className="fixed inset-0 z-[10000] flex flex-col bg-background text-foreground"
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-modal-title"
        >
            <header
                className="sticky top-0 z-[1] min-h-14 border-b sm:min-h-16"
                style={{ backgroundColor: 'hsl(var(--card))', borderColor: BAR_BORDER }}
            >
                <div className="mx-auto flex h-full min-h-14 w-full max-w-screen-xl items-center justify-between gap-3 pr-2 sm:min-h-16">
                    <button
                        type="button"
                        onClick={handleCloseClick}
                        className="inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-foreground transition-colors hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label={t`Close checkout`}
                    >
                        <X className="h-6 w-6" aria-hidden />
                    </button>
                    <div className="min-w-0 flex-1" aria-hidden />
                    <div id="checkout-modal-title" className="text-right">
                        <p className="text-base font-medium text-foreground">{stepIndicator}</p>
                        <p className="text-sm text-muted-foreground">
                            <Trans>Subtotal: {formatCurrency(subtotal)}</Trans>
                        </p>
                    </div>
                </div>
            </header>

            {showControlBar && (
                <div className="h-px w-full shrink-0 bg-[rgba(255,255,255,0.12)]" aria-hidden />
            )}

            {showControlBar && (
                <div
                    className="sticky top-14 z-[1] border-b py-2 sm:top-16"
                    style={{ backgroundColor: 'hsl(var(--card))', borderColor: BAR_BORDER }}
                >
                    <div className="mx-auto flex w-full max-w-screen-xl items-center px-2">
                        <div className="flex min-w-0 flex-[2] items-center sm:flex-1">
                            {showBackButton && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={onBack}
                                    className="h-10 gap-2 border-border bg-accent px-4 text-foreground shadow-none hover:bg-muted"
                                >
                                    <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
                                    <Trans>Back</Trans>
                                </Button>
                            )}
                        </div>
                        <div className="flex min-w-0 flex-[3] items-center justify-center sm:flex-1">
                            {timer ? (
                                <span className="font-mono text-base text-muted-foreground">
                                    {timer}
                                </span>
                            ) : null}
                        </div>
                        <div className="flex min-w-0 flex-[6] items-center justify-end gap-2 sm:flex-1 sm:gap-3">
                            {showReviewCart && (
                                <Button
                                    type="button"
                                    variant="default"
                                    onClick={onReviewCart}
                                    disabled={seatsPickedCount === 0}
                                    className="h-10 gap-2 px-4 font-bold uppercase tracking-wide shadow-none disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100"
                                >
                                    <ShoppingCart className="h-4 w-4 shrink-0" aria-hidden />
                                    <Trans>
                                        Review Cart
                                        {seatsPickedCount ? ` (${seatsPickedCount})` : ''}
                                    </Trans>
                                </Button>
                            )}
                            {showContinueButton && (
                                <Button
                                    type="button"
                                    variant="default"
                                    onClick={onContinue}
                                    disabled={disableContinue}
                                    className="h-10 px-4 font-bold uppercase tracking-wide shadow-none disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 sm:ml-2"
                                >
                                    <Trans>Continue</Trans>
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="checkout-modal-scrollbar min-h-0 flex-1 overflow-auto">
                {props.children}
            </div>
        </div>
    ) : null;

    return (
        <>
            <ConfirmationDialog
                open={confirmDialogOpen}
                onClose={handleCancelClose}
                onConfirm={handleConfirmClose}
                title={t`Leave Checkout?`}
                message={t`Are you sure you want to leave? Your seat selections will be released and your cart will be cleared. You will need to start over.`}
                confirmLabel={t`Leave`}
                cancelLabel={t`Stay`}
            />
            {modal ? createPortal(modal, document.body) : null}
        </>
    );
}
